import React, { useState, useEffect } from "react";
import { Card } from "primereact/card";
import { BreadCrumb } from "primereact/breadcrumb";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import { HOME_ROUTE_PATH } from "../app_utils/route_paths/resolver/PageRoutes";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { PrimeIcons } from "primereact/api";
import { useHistory } from "react-router-dom";
import { copyToClipboard } from "../app_utils/utils/Utils";

const ApplicationsView = () => {
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const history = useHistory();

  const breadcrumbHome = {
    icon: "pi pi-home",
    command: () => {
      window.location.href = HOME_ROUTE_PATH;
    },
  };

  useEffect(() => {
    setIsLoading(true);
    new BaseApiServiceImpl("/api/v1/api-keys")
      .getRequestWithJsonResponse({ offset: 0, limit: 100 })
      .then((response) => {
        const raw = response?.records || response?.data || response || [];
        setApiKeys(Array.isArray(raw) ? raw : []);
      })
      .catch(() => setApiKeys([]))
      .finally(() => setIsLoading(false));
  }, []);

  const getStatusSeverity = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "deactivated":
        return "danger";
      case "revoked":
        return "danger";
      case "expired":
        return "warning";
      default:
        return "info";
    }
  };

  const copyKey = (key: string) => {
    copyToClipboard(key);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
  };

  return (
    <div className="grid">
      <div className="col-12">
        <Card>
          <BreadCrumb home={breadcrumbHome} model={[{ label: "Applications" }]} />
          <div className="flex justify-content-between align-items-center mb-3">
            <div>
              <h2 style={{ margin: "1rem 0 0.25rem" }}>Applications</h2>
              <p style={{ color: "#64748b", marginBottom: 0 }}>
                Registered applications and API keys accessing this platform.
              </p>
            </div>
            <Button
              label="Manage API Keys"
              icon={PrimeIcons.KEY}
              onClick={() => history.push("/api-keys")}
              className="p-button-outlined"
            />
          </div>

          {isLoading ? (
            <div className="flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
              <ProgressSpinner style={{ width: "50px", height: "50px" }} />
            </div>
          ) : (
            <DataTable
              value={apiKeys}
              paginator
              rows={10}
              responsiveLayout="scroll"
              emptyMessage="No applications registered yet. Create an API key to get started."
              className="mt-3"
            >
              <Column field="name" header="Application Name" sortable />
              <Column
                header="API Key"
                body={(rowData: any) => {
                  const fullKey = rowData.key || "";
                  const prefix = fullKey.length > 8 ? fullKey.substring(0, 8) + "..." : fullKey;
                  return (
                    <div className="flex align-items-center gap-2">
                      <code style={{ fontSize: "0.8rem", background: "#f1f5f9", padding: "0.25rem 0.5rem", borderRadius: "4px" }}>
                        {prefix || "****"}
                      </code>
                      {fullKey && (
                        <Button
                          icon={PrimeIcons.COPY}
                          className="p-button-text p-button-sm"
                          onClick={() => copyKey(fullKey)}
                          tooltip="Copy full key"
                        />
                      )}
                    </div>
                  );
                }}
              />
              <Column
                header="Status"
                body={(rowData: any) => (
                  <Tag value={rowData.status || "Active"} severity={getStatusSeverity(rowData.status)} />
                )}
              />
              <Column
                header="Last Used"
                body={(rowData: any) => (rowData.lastUsedAt ? formatDate(rowData.lastUsedAt) : "Never")}
                sortable
              />
              <Column
                header="Usage Count"
                body={(rowData: any) => rowData.usageCount ?? 0}
                sortable
              />
              <Column
                header="Created"
                body={(rowData: any) => (rowData.dateCreated ? formatDate(rowData.dateCreated) : "-")}
                sortable
              />
            </DataTable>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ApplicationsView;
